import { calculatePrice } from './calculatePrice'
import { PricingOptionType } from '../../constants'

const createFinalCostSelector = (unitNumberKey: string, pricingOptionKey: string) => [
    (s) => [s[unitNumberKey], s[pricingOptionKey]],
    (unitNumber: number, pricingOption: PricingOptionType) => {
        return calculatePrice(unitNumber, pricingOption)
    },
]

const createFixedCostSelector = (unitNumberKey: string, fixedPricingOption: PricingOptionType) => [
    (s) => [s[unitNumberKey]],
    (unitNumber: number) => {
        return calculatePrice(unitNumber, fixedPricingOption)
    },
]

const createMonthlyTotalSelector = (keys: string[]) => [
    (s) => keys.map((key) => s[key]),
    (...costs: number[]) => {
        return costs.reduce((acc, cost) => acc + cost, 0)
    },
]

export const selectors = () => ({
    //property management
    propertymanagementcloudCost: createFinalCostSelector('propertyManagementUnitNumber', 'pricingOption'),
    propertymanagementcloudStandardCost: createFixedCostSelector(
        'propertyManagementUnitNumber',
        'propertymanagementcloud-standard'
    ),
    propertymanagementcloudProfessionalCost: createFixedCostSelector(
        'propertyManagementUnitNumber',
        'propertymanagementcloud-professional'
    ),
    propertymanagementcloudEnterpriseCost: createFixedCostSelector(
        'propertyManagementUnitNumber',
        'propertymanagementcloud-enterprise'
    ),
    propertymanagementOnPremiseStandardCost: createFixedCostSelector(
        'propertyManagementUnitNumber',
        'property-management-on-premise-standard'
    ),
    onPremiseProfessionalCost: createFixedCostSelector('propertyManagementUnitNumber', 'on-premise-professional'),
    finalCost: createFinalCostSelector('propertyManagementUnitNumber', 'pricingOption'),
    propertymanagementOnPremiseEnterpriseCost: createFixedCostSelector(
        'propertyManagementUnitNumber',
        'property-management-on-premise-enterprise'
    ),
    //crm
    realestateCrmCost: createFinalCostSelector('realestateCrmUserNumber', 'crmPricingOption'),
    realestateCrmStandardCost: createFixedCostSelector('realestateCrmUserNumber', 'real-estate-crm-cloud-standard'),
    realestateCrmProfessionalCost: createFixedCostSelector(
        'realestateCrmUserNumber',
        'real-estate-crm-cloud-professional'
    ),
    realestateCrmEnterpriseCost: createFixedCostSelector('realestateCrmUserNumber', 'real-estate-crm-cloud-enterprise'),
    //marketing
    realestateMarketingCost: createFinalCostSelector('realestateMarketingUserNumber', 'marketingPricingOption'),
    realestateMarketingStandardCost: createFixedCostSelector(
        'realestateMarketingUserNumber',
        'real-estate-marketing-cloud-standard'
    ),
    realestateMarketingProfessionalCost: createFixedCostSelector(
        'realestateMarketingUserNumber',
        'real-estate-marketing-cloud-professional'
    ),
    realestateMarketingEnterpriseCost: createFixedCostSelector(
        'realestateMarketingUserNumber',
        'real-estate-marketing-cloud-enterprise'
    ),
    //marketing contacts
    marketingContactsCost: createFinalCostSelector('marketingContactsUserNumber', 'marketingContactsPricingOption'),
    marketingContactsStandardCost: createFixedCostSelector(
        'marketingContactsUserNumber',
        'marketing-contacts-cloud-standard'
    ),
    marketingContactsProfessionalCost: createFixedCostSelector(
        'marketingContactsUserNumber',
        'marketing-contacts-cloud-professional'
    ),
    marketingContactsEnterpriseCost: createFixedCostSelector(
        'marketingContactsUserNumber',
        'marketing-contacts-cloud-enterprise'
    ),
    //marketing website
    marketingWebsiteCost: createFinalCostSelector('marketingWebsiteUserNumber', 'marketingWebsitePricingOption'),
    marketingWebsiteStandardCost: createFixedCostSelector(
        'marketingWebsiteUserNumber',
        'marketing-website-cloud-standard'
    ),
    marketingWebsiteProfessionalCost: createFixedCostSelector(
        'marketingWebsiteUserNumber',
        'marketing-website-cloud-professional'
    ),
    marketingWebsiteEnterpriseCost: createFixedCostSelector(
        'marketingWebsiteUserNumber',
        'marketing-website-cloud-enterprise'
    ),
    //marketing apps
    marketingAppCost: createFinalCostSelector('marketingAppUserNumber', 'marketingAppPricingOption'),
    marketingAppStandardCost: createFixedCostSelector('marketingAppUserNumber', 'marketing-app-cloud-standard'),
    marketingAppProfessionalCost: createFixedCostSelector('marketingAppUserNumber', 'marketing-app-cloud-professional'),
    marketingAppEnterpriseCost: createFixedCostSelector('marketingAppUserNumber', 'marketing-app-cloud-enterprise'),
    //final costs
    monthlyTotal: createMonthlyTotalSelector([
        'propertymanagementcloudCost',
        'realestateCrmCost',
        'marketingContactsCost',
        'marketingWebsiteCost',
        'marketingAppCost',
    ]),
    monthlyStandardTotal: createMonthlyTotalSelector([
        'propertymanagementcloudStandardCost',
        'realestateCrmStandardCost',
        'marketingContactsStandardCost',
        'marketingWebsiteStandardCost',
        'marketingAppStandardCost',
    ]),
    monthlyProfessionalTotal: createMonthlyTotalSelector([
        'propertymanagementcloudProfessionalCost',
        'realestateCrmProfessionalCost',
        'marketingContactsProfessionalCost',
        'marketingWebsiteProfessionalCost',
        'marketingAppProfessionalCost',
    ]),
    monthlyEnterpriseTotal: createMonthlyTotalSelector([
        'propertymanagementcloudEnterpriseCost',
        'realestateCrmEnterpriseCost',
        'marketingContactsEnterpriseCost',
        'marketingWebsiteEnterpriseCost',
        'marketingAppEnterpriseCost',
    ]),
    finalMonthlyCost: [
        (s) => [s.monthlyTotal],
        (monthlyTotal: number) => {
            return monthlyTotal.toLocaleString()
        },
    ],
    finalMonthlyStandardTotal: [
        (s) => [s.monthlyStandardTotal],
        (monthlyStandardTotal: number) => {
            return monthlyStandardTotal.toLocaleString()
        },
    ],
    finalMonthlyProfessionalTotal: [
        (s) => [s.monthlyProfessionalTotal],
        (monthlyProfessionalTotal: number) => {
            return monthlyProfessionalTotal.toLocaleString()
        },
    ],
    finalMonthlyEnterpriseTotal: [
        (s) => [s.monthlyEnterpriseTotal],
        (monthlyEnterpriseTotal: number) => {
            return monthlyEnterpriseTotal.toLocaleString()
        },
    ],
    finalAnnualCost: [
        (s) => [s.monthlyTotal],
        (monthlyTotal: number) => {
            const finalAnnualCost = monthlyTotal * 10.8
            return finalAnnualCost.toLocaleString()
        },
    ],
    finalAnnualStandardTotal: [
        (s) => [s.monthlyStandardTotal],
        (monthlyStandardTotal: number) => {
            const finalAnnualStandardTotal = monthlyStandardTotal * 10.8
            return finalAnnualStandardTotal.toLocaleString()
        },
    ],
    finalAnnualProfessionalTotal: [
        (s) => [s.monthlyProfessionalTotal],
        (monthlyProfessionalTotal: number) => {
            const finalAnnualProfessionalTotal = monthlyProfessionalTotal * 10.8
            return finalAnnualProfessionalTotal.toLocaleString()
        },
    ],
    finalAnnualEnterpriseTotal: [
        (s) => [s.monthlyEnterpriseTotal],
        (monthlyEnterpriseTotal: number) => {
            const finalAnnualEnterpriseTotal = monthlyEnterpriseTotal * 10.8
            return finalAnnualEnterpriseTotal.toLocaleString()
        },
    ],
})
